import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useState, useEffect, useMemo} from 'react';
import { Link, useParams } from 'react-router-dom';
import '@dialectlabs/blinks/index.css';
import {useBlink} from '@dialectlabs/blinks';
import {useBlinkSolanaWalletAdapter} from '@dialectlabs/blinks/hooks/solana';
import {Blink} from '@dialectlabs/blinks';
import {useGateAccess} from "../hooks/useGateAccess";
import ComponentControls from "./ComponentControls";
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from "./TimestampBadge";
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { buildPanelClassName } from '../utils/panelTheme';
import { getYouTubeEmbedUrl } from '../utils/youtubeEmbed';

export default function LinkComponent({
                                          comp,
                                          index,
                                          moveUp,
                                          moveDown,
                                          onEdit,
                                          isEditor = false,
                                          removeComponent,
                                          gateType,
                                          selectedListId,
                                          onGateTypeChange,
                                          onGateListChange,
                                          extraGates,
                                          gateLogic,
                                          onExtraGatesChange,
                                          onGateLogicChange,
                                          donationCampaignOptions = [],
                                          donationGate,
                                          onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,

                                          style = {},
                                          onStyleChange,
                                          coverUrl = '',
                                          onCoverChange,
                                          onPanelBackgroundImageChange,
                                          timestampDisplay = 'none',
                                          onTimestampDisplayChange,

                                          // NEW (optional, no renames)
                                          pageTypography = null,
                                          ownerPublicKey = '',
                                      }) {
    const {url, title, imageUrl} = comp;
    const { username: routeUsername, componentId: routeComponentId } = useParams();
    // prepare and fetch blink instance
    const rpcUrl = process.env.REACT_APP_RPC_ENDPOINT;
    const {adapter} = useBlinkSolanaWalletAdapter(rpcUrl);

    let blinkApiUrl = url;
    if (url.includes('/blinks/') && !url.endsWith('/actions.json')) {
        blinkApiUrl = url.replace(/\/?$/, '') + '/actions.json';
    }
    const {blink, isLoadingBlink, error} = useBlink({url: blinkApiUrl});
    const isBlink = Boolean(blink);
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate = normalizeGate(comp.gate || {type: 'public', listId: ''});
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );
    const [gateRefreshKey, setGateRefreshKey] = useState(0);

    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const logic = comp.gateLogic === 'all' ? 'all' : 'any';
    const [hasAccess, isLoading, gateNamesLabel, gateNames, gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const safeUsername = routeUsername || comp?.muultiName || '';
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);
    const detailHref =
        safeUsername && comp?.id
            ? `/${safeUsername}/l/${comp.id}`
            : null;

    // global + panel typography (panel overrides win)
    const typo = resolveTypography(pageTypography, style);
    const numToPx = v => (typeof v === 'number' ? `${v}px` : (v || undefined));

    // derive link color from style but prefer explicit link typography
    const linkColor = typo.links.color || style.color || 'inherit';
    const linkDecoration = typo.links.textDecoration || 'none';
    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );
    const youtubeEmbedUrl = useMemo(() => getYouTubeEmbedUrl(url), [url]);
    const canLinkToDetail =
        !!youtubeEmbedUrl &&
        !!detailHref &&
        String(routeComponentId || '') !== String(comp?.id || '');

    if (isLoading) {
        return <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
            <h3
                style={{
                    fontFamily: typo.headers.fontFamily,
                    fontSize: numToPx(typo.headers.fontSize),
                    fontWeight: typo.headers.fontWeight,
                    color: typo.headers.color,
                    lineHeight: typo.headers.lineHeight
                }}
            >
                Checking access for {gateNamesLabel}…
            </h3>
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {analyticsFooter}
            <ComponentControls
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
            /></div>
        </GateRevealPanel>;
    }

    if (!hasAccess) {
        const directPaymentGate = (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate = (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                <h1
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    {deniedHeading} - {gateNamesLabel}
                    {passwordGate ? (
                        <PasswordUnlock
                            gate={passwordGate}
                            componentId={comp.id}
                            onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                        />
                    ) : null}
                    {directPaymentGate ? (
                        <DirectPaymentUnlock
                            gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                            onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                        />
                    ) : null}
                </h1>
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>

            {youtubeEmbedUrl ? (
                <div className="component-youtube-embed">
                    {title && (
                        <h1
                            style={{
                                fontFamily: typo.headers.fontFamily,
                                fontSize: numToPx(typo.headers.fontSize),
                                fontWeight: typo.headers.fontWeight,
                                color: typo.headers.color,
                                lineHeight: typo.headers.lineHeight,
                                margin: 0
                            }}
                        >
                            {title}
                        </h1>
                    )}
                    <div className="component-youtube-embed-frame">
                        <iframe
                            src={youtubeEmbedUrl}
                            title={title || 'YouTube video'}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : isBlink ? (
                isLoadingBlink ? (
                    <p
                        style={{
                            fontFamily: typo.paragraph.fontFamily,
                            fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color,
                            lineHeight: typo.paragraph.lineHeight
                        }}
                    >
                        Loading blink…
                    </p>
                ) : error ? (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            ...style,
                            display: 'flex',
                            alignItems: 'center',
                            color: linkColor,
                            textDecoration: linkDecoration
                        }}
                    >
                        {title && (
                            <h1
                                style={{
                                    fontFamily: typo.headers.fontFamily,
                                    fontSize: numToPx(typo.headers.fontSize),
                                    fontWeight: typo.headers.fontWeight,
                                    color: typo.headers.color,
                                    lineHeight: typo.headers.lineHeight,
                                    margin: 0
                                }}
                            >
                                {title}
                            </h1>
                        )}
                    </a>
                ) : (
                    <Blink
                        blink={blink}
                        adapter={adapter}
                        websiteText={new URL(url).hostname}
                        stylePreset="default"
                        className="component-blink"
                    />
                )
            ) : (

                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: linkColor,
                        textDecoration: linkDecoration,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                    className="link-component-link"
                >

                    {title && (
                        <h1
                            style={{
                                fontFamily: typo.headers.fontFamily,
                                fontSize: typo.headers.fontSize,
                                fontWeight: typo.headers.fontWeight,
                                color: typo.headers.color,
                                lineHeight: typo.headers.lineHeight,
                                margin: 0
                            }}
                        >
                            {title}
                        </h1>
                    )}
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt={title || 'Link image'}
                            style={{
                                width: '100%',
                                borderRadius: '8px',
                                marginBottom: title ? '8px' : 0
                            }}
                        />
                    )}

                </a>
            )}

            {canLinkToDetail ? (
                <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center' }}>
                    <Link
                        to={detailHref}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            fontFamily: typo.links.fontFamily,
                            fontSize: typeof typo.links.fontSize === 'number' ? `${typo.links.fontSize}px` : typo.links.fontSize,
                            fontWeight: typo.links.fontWeight,
                            color: typo.links.color,
                            textDecoration: typo.links.textDecoration || 'underline',
                            lineHeight: typo.links.lineHeight || 'normal',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer'
                        }}
                    >
                        Open in new page ->
                    </Link>
                </div>
            ) : null}

            {hasAccess && (
                <GateBadge  gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {analyticsFooter}
            <ComponentControls
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || 'none'}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
            />
        </div>
        </GateRevealPanel>
    );
}


